import { useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  currentPinningSessionAtom,
  pinningFeedbackLastEditedPinIdAtom,
  pinsAtom,
  useCreatePinningFeedback
} from "@design-lab/entities/pinning";
import { useToast } from "@design-lab/shared/ui";

const SUCCESS_COLLAPSE_DELAY_MS = 1500;

export type PinningFeedbackStatus = "idle" | "sending" | "success" | "error";

export function usePinningFeedback() {
  const currentSession = useAtomValue(currentPinningSessionAtom);
  const pins = useAtomValue(pinsAtom);
  const lastEditedPinId = useAtomValue(pinningFeedbackLastEditedPinIdAtom);

  const [positive, setPositive] = useState<boolean | null>(null);
  const [message, setMessage] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<PinningFeedbackStatus>("idle");

  const { error: showErrorToast } = useToast();
  const { mutate: submitFeedback } = useCreatePinningFeedback();
  const successTimeoutRef = useRef<number | null>(null);

  const lastEditedPin = useMemo(
    () => pins.find(pin => pin.id === lastEditedPinId) ?? null,
    [pins, lastEditedPinId]
  );

  const resetFeedback = useCallback(() => {
    setPositive(null);
    setMessage("");
    setIsOpen(false);
    setStatus("idle");
  }, [setIsOpen, setMessage, setPositive, setStatus]);

  const handleSelectPositive = useCallback(() => {
    setPositive(true);
    setIsOpen(true);
    setStatus("idle");
  }, [setIsOpen, setPositive, setStatus]);

  const handleSelectNegative = useCallback(() => {
    setPositive(false);
    setIsOpen(true);
    setStatus("idle");
  }, [setIsOpen, setPositive, setStatus]);

  const handleCancel = useCallback(() => {
    resetFeedback();
  }, [resetFeedback]);

  const handleSubmit = useCallback(() => {
    if (positive === null) return;

    const sessionId = currentSession?.sessionId;
    if (!sessionId) {
      showErrorToast({
        title: "Unable to submit feedback",
        description: "Missing session information."
      });
      return;
    }

    setStatus("sending");

    submitFeedback(
      {
        sessionId,
        feedback: {
          positive,
          message: message.trim() ? message.trim() : null,
          currentPin: lastEditedPin?.id ?? null,
          currentPinState: lastEditedPin?.state ?? null
        }
      },
      {
        onSuccess: () => {
          setStatus("success");
        },
        onError: () => {
          setStatus("error");
          showErrorToast({
            title: "Feedback failed",
            description: "Something went wrong. Please try again."
          });
        }
      }
    );
  }, [
    positive,
    currentSession?.sessionId,
    showErrorToast,
    submitFeedback,
    message,
    lastEditedPin?.id,
    lastEditedPin?.state,
    setStatus
  ]);

  useEffect(() => {
    if (status !== "success") return;

    if (successTimeoutRef.current) {
      window.clearTimeout(successTimeoutRef.current);
    }

    successTimeoutRef.current = window.setTimeout(() => {
      resetFeedback();
    }, SUCCESS_COLLAPSE_DELAY_MS);

    // eslint-disable-next-line consistent-return
    return () => {
      if (successTimeoutRef.current) {
        window.clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
    };
  }, [status, resetFeedback]);

  return {
    positive,
    message,
    isOpen,
    status,
    handleSelectPositive,
    handleSelectNegative,
    handleCancel,
    handleSubmit,
    setMessage
  };
}
